import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Shield, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const MFASetup = () => {
  const [mfaStatus, setMfaStatus] = useState<any>(null);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [qrCode, setQrCode] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check MFA enrollment status
      const { data: factors } = await supabase.auth.mfa.listFactors();
      
      // Check database MFA status
      const { data: dbStatus } = await supabase
        .from('mfa_status')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setMfaStatus({
        enrolled: factors?.totp && factors.totp.length > 0,
        enforced: dbStatus?.mfa_enforced || false,
        factors: factors?.totp || []
      });
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const enrollMFA = async () => {
    setIsEnrolling(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Enroll a new TOTP factor
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: user.email || 'Primary Device'
      });

      if (error) throw error;

      if (data) {
        setQrCode(data.totp.qr_code);
        toast({
          title: "MFA Enrollment Started",
          description: "Scan the QR code with your authenticator app",
        });
      }
    } catch (error: any) {
      toast({
        title: "Enrollment Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const factorId = factors?.totp?.[0]?.id;
      
      if (!factorId) throw new Error("No MFA factor found");

      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verifyCode
      });

      if (error) throw error;

      // Update database status
      const { data: { user } } = await supabase.auth.getUser();
      await supabase
        .from('mfa_status')
        .upsert({
          user_id: user?.id,
          mfa_enabled: true
        });

      toast({
        title: "2FA Enabled Successfully",
        description: "Your account is now protected with two-factor authentication",
      });

      setQrCode("");
      setVerifyCode("");
      checkMFAStatus();
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const disableMFA = async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const factorId = factors?.totp?.[0]?.id;
      
      if (!factorId) throw new Error("No MFA factor found");

      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;

      // Update database status
      const { data: { user } } = await supabase.auth.getUser();
      await supabase
        .from('mfa_status')
        .update({ mfa_enabled: false })
        .eq('user_id', user?.id);

      toast({
        title: "2FA Disabled",
        description: "Two-factor authentication has been disabled",
      });

      checkMFAStatus();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Two-Factor Authentication (2FA)
        </CardTitle>
        <CardDescription>
          Add an extra layer of security to your account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {mfaStatus?.enforced && !mfaStatus?.enrolled && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              2FA is required for admin accounts. Please enable it now.
            </AlertDescription>
          </Alert>
        )}

        {mfaStatus?.enrolled ? (
          <div className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Two-factor authentication is enabled and protecting your account
              </AlertDescription>
            </Alert>
            
            {!mfaStatus?.enforced && (
              <Button
                variant="destructive"
                onClick={disableMFA}
              >
                Disable 2FA
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {!qrCode ? (
              <Button
                onClick={enrollMFA}
                disabled={isEnrolling}
              >
                {isEnrolling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  "Enable 2FA"
                )}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  <img src={qrCode} alt="QR Code" className="mx-auto" />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="verify-code">Enter 6-digit code from your authenticator app</Label>
                  <Input
                    id="verify-code"
                    type="text"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                  />
                </div>
                
                <Button
                  onClick={verifyAndEnable}
                  disabled={isVerifying || verifyCode.length !== 6}
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Enable"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
